import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import ArchivedTooltip from "@components/companies/tombstone/archived-tooltip";
import AuthContext from "@contexts/auth-context";
import { formatMarketCap, truncateString } from "@utils/helpers/formats";
import { compact, uniq } from "lodash";
import React, { useCallback, useContext } from "react";
var CompanyTypeEnum;
(function(CompanyTypeEnum) {
    CompanyTypeEnum["PrivateBiopharma"] = "Private Biopharma";
    CompanyTypeEnum["PublicBiopharma"] = "Public Biopharma";
    CompanyTypeEnum["NonBiopharma"] = "Non-biopharma";
    CompanyTypeEnum["AcquiredOrMerged"] = "M&A";
})(CompanyTypeEnum || (CompanyTypeEnum = {}));
var getCompanyStatus = function(param) {
    var IsArchived = param.IsArchived, OwnershipStatus = param.OwnershipStatus, CompanyType = param.CompanyProfileCategory, EventType = param.EventType;
    var type = CompanyTypeEnum[Object.keys(CompanyTypeEnum).find(function(t) {
        return CompanyTypeEnum[t] === CompanyType;
    })];
    if (type === CompanyTypeEnum.PrivateBiopharma || type === CompanyTypeEnum.PublicBiopharma) {
        if (IsArchived) {
            return "Archived";
        } else if (EventType == "Out of Business") {
            return "Out of Business";
        } else {
            return OwnershipStatus;
        }
    } else if (type == CompanyTypeEnum.NonBiopharma) {
        if (IsArchived) {
            return "Archived";
        } else if (EventType == "Out of Business") {
            return "Out of Business";
        } else {
            return CompanyType;
        }
    } else if (type === CompanyTypeEnum.AcquiredOrMerged) {
        if (EventType == "Acquisition") {
            return "Acquired";
        } else if (EventType == "Reverse Merger") {
            return "Reverse-merged";
        } else if (EventType == "Merger") {
            return "Merged";
        } else if (IsArchived) {
            return "Archived";
        }
    }
    return null;
};
var tombstoneMapping = function(data, key) {
    var _a, _b;
    switch(key){
        case "headquarters":
            var headquarters = uniq(compact([
                data === null || data === void 0 ? void 0 : data.City,
                data === null || data === void 0 ? void 0 : data.State,
                data === null || data === void 0 ? void 0 : data.Country
            ]));
            return headquarters.length > 0 ? headquarters.join(", ") : "N/A";
        case "website":
            return data === null || data === void 0 ? void 0 : data.CompanyUrl;
        case "companyStatus":
        case "ownershipStatus":
            return getCompanyStatus(data);
        case "yearFounded":
            return data === null || data === void 0 ? void 0 : data.YearFounded;
        case "exchangeTicker1":
            return (data === null || data === void 0 ? void 0 : data.Exchange) ? "".concat(data.Exchange, ":").concat(data === null || data === void 0 ? void 0 : data.Ticker) : "N/A";
        case "exchangeTicker2":
            return (data === null || data === void 0 ? void 0 : data.Exchange2) ? "".concat(data.Exchange2, ":").concat(data === null || data === void 0 ? void 0 : data.Ticker2) : "N/A";
        case "marketCap":
            return data === null || data === void 0 ? void 0 : data.MarketCap;
        case "sector":
            return data === null || data === void 0 ? void 0 : data.Sector;
        case "ceo":
            return data === null || data === void 0 ? void 0 : data.CEO;
        case "numberOfEmployees":
            return (_a = data === null || data === void 0 ? void 0 : data.NumberOfEmployees) === null || _a === void 0 ? void 0 : _a.toLocaleString();
        case "totalEnterpriseValue":
            return formatMarketCap(data === null || data === void 0 ? void 0 : data.TotalEnterpriseValue);
        case "peRatio":
            return (_b = data === null || data === void 0 ? void 0 : data.PERatio) === null || _b === void 0 ? void 0 : _b.toString();
        default:
            return null;
    }
};
var Tombstone = function(param) {
    var _companyData = param.companyData, annualReportDate = _companyData.annualReportDate, companyNameWdba = _companyData.companyNameWdba, ecpArticles = _companyData.ecpArticles, archived = _companyData.archived, companyType = _companyData.companyType, tombstone = _companyData.tombstone, tombstoneComponents = param.tombstoneComponents;
    var _a;
    var currentUser = useContext(AuthContext);
    var dealUrl = currentUser ? "".concat(process.env.NEXT_PUBLIC_BCIQ_URL, "/deal/all-deals/insights") : "".concat(process.env.NEXT_PUBLIC_BIOCENTURY_URL, "/subscribe/trial?type=3");
    var financingUrl = currentUser ? "".concat(process.env.NEXT_PUBLIC_BCIQ_URL, "/finance/financing/insights") : "".concat(process.env.NEXT_PUBLIC_BIOCENTURY_URL, "/subscribe/trial?type=3");
    var ecpArticle = (ecpArticles === null || ecpArticles === void 0 ? void 0 : ecpArticles[0]) || null;
    var tombstoneComponent = tombstoneComponents["".concat(companyType !== null && companyType !== void 0 ? companyType : "Default").concat(archived ? "_archived" : "")];
    var ref = tombstoneComponent !== null && tombstoneComponent !== void 0 ? tombstoneComponent : {}, tombstoneDataElements = ref.tombstoneDataElements, _publicTombstoneDataElements = ref.publicTombstoneDataElements, publicTombstoneDataElements = _publicTombstoneDataElements === void 0 ? [] : _publicTombstoneDataElements, tombstoneMessage = ref.tombstoneMessage, iconMessaging = ref.iconMessaging, ecpMessage = ref.ecpMessage;
    var dealsComponent = '\n		<a href="'.concat(dealUrl, '" rel="noreferrer" target="_blank">\n			<span class="text-blue-800 underline cursor-pointer">Deals</span>\n		</a>\n	');
    var financingComponent = '\n		<a href="'.concat(financingUrl, '" rel="noreferrer" target="_blank">\n			<span class="text-blue-800 underline cursor-pointer">Financing</span>\n		</a>\n	');
    var ecpComponent = ecpArticle ? '\n			<a class="-mr-1" href="'.concat(process.env.NEXT_PUBLIC_BIOCENTURY_URL).concat(ecpArticle.url, '" rel="noreferrer" target="_blank">\n				<span class="text-blue-800 underline cursor-pointer">Emerging Company Profile</span>\n			</a>\n		') : null;
    var ecpHtml = ((ecpMessage === null || ecpMessage === void 0 ? void 0 : ecpMessage.html) || "").replace(/&lt;ECP&gt;/, ecpComponent).trim();
    var updatedHtml = ((tombstoneMessage === null || tombstoneMessage === void 0 ? void 0 : tombstoneMessage.html) || "").replace(/&lt;DEALS&gt;/, dealsComponent).replace(/&lt;FINANCING&gt;/, financingComponent).trim();
    var getTombstoneDataElements = useCallback(function(tombstoneUIElement) {
        var value = tombstoneMapping(tombstone.Item, tombstoneUIElement);
        if (value === "Archived" && tombstoneUIElement === "ownershipStatus") {
            return /*#__PURE__*/ _jsxs("span", {
                className: "flex items-center",
                children: [
                    /*#__PURE__*/ _jsx("span", {
                        className: "mr-1",
                        children: "Archived"
                    }),
                    " ",
                    /*#__PURE__*/ _jsx(ArchivedTooltip, {
                        messaging: (iconMessaging === null || iconMessaging === void 0 ? void 0 : iconMessaging.html) || ""
                    })
                ]
            });
        }
        if (tombstoneUIElement === "website") {
            if (value) {
                return /*#__PURE__*/ _jsx("a", {
                    href: value,
                    rel: "noreferrer",
                    target: "_blank",
                    children: /*#__PURE__*/ _jsx("span", {
                        className: "text-blue-800 underline cursor-pointer",
                        children: truncateString(value, 30)
                    })
                });
            } else {
                return /*#__PURE__*/ _jsx("span", {
                    children: "N/A"
                });
            }
        }
        return /*#__PURE__*/ _jsx("span", {
            children: value || "N/A"
        });
    }, [
        archived,
        iconMessaging === null || iconMessaging === void 0 ? void 0 : iconMessaging.html,
        tombstone
    ]);
    return /*#__PURE__*/ _jsxs("div", {
        className: "bg-white w-full mb-2",
        children: [
            /*#__PURE__*/ _jsx("div", {
                className: "p-2 font-bold text-2xl",
                children: /*#__PURE__*/ _jsx("h1", {
                    children: companyNameWdba
                })
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3",
                children: [
                    /*#__PURE__*/ _jsx("div", {
                        className: "p-2 col-span-1 grid grid-cols-1 gap-1 mr-10 ",
                        children: tombstoneDataElements === null || tombstoneDataElements === void 0 ? void 0 : tombstoneDataElements.map(function(param, index) {
                            var tombstoneUIElement = param.tombstoneUIElement, label = param.label, isAdditioanl = param.isAdditioanl;
                            return !isAdditioanl && /*#__PURE__*/ _jsxs("div", {
                                className: "flex",
                                children: [
                                    /*#__PURE__*/ _jsxs("span", {
                                        className: "text-sm font-bold mr-2",
                                        children: [
                                            label ? label : tombstoneUIElement,
                                            ":",
                                            " "
                                        ]
                                    }),
                                    getTombstoneDataElements(tombstoneUIElement)
                                ]
                            }, index);
                        })
                    }),
                    companyType === "PublicBiopharma" && /*#__PURE__*/ _jsx("div", {
                        className: "p-2 col-span-1 grid grid-cols-1 gap-1 mr-10 ",
                        children: tombstoneDataElements === null || tombstoneDataElements === void 0 ? void 0 : tombstoneDataElements.map(function(param, index) {
                            var tombstoneUIElement = param.tombstoneUIElement, label = param.label, isAdditioanl = param.isAdditioanl, toShowAnnualReportDate = param.toShowAnnualReportDate;
                            return isAdditioanl && /*#__PURE__*/ _jsxs("div", {
                                title: toShowAnnualReportDate ? "Data as of the Latest Annual Report: ".concat(annualReportDate) : "",
                                className: "flex",
                                children: [
                                    /*#__PURE__*/ _jsxs("span", {
                                        className: "text-sm font-bold mr-2",
                                        children: [
                                            label ? label : tombstoneUIElement,
                                            ":",
                                            " "
                                        ]
                                    }),
                                    getTombstoneDataElements(tombstoneUIElement)
                                ]
                            }, index);
                        })
                    }),
                    companyType === "PublicBiopharma" && /*#__PURE__*/ _jsx("div", {
                        className: "p-2 col-span-1 grid grid-cols-1 gap-1 mr-10",
                        children: publicTombstoneDataElements.map(function(param, index) {
                            var tombstoneUIElement = param.tombstoneUIElement, label = param.label;
                            return /*#__PURE__*/ _jsxs("div", {
                                className: "flex",
                                children: [
                                    /*#__PURE__*/ _jsxs("span", {
                                        className: "text-sm font-bold mr-2",
                                        children: [
                                            label ? label : tombstoneUIElement,
                                            ":",
                                            " "
                                        ]
                                    }),
                                    tombstoneUIElement === "marketCap" && /*#__PURE__*/ _jsx("span", {
                                        children: formatMarketCap(parseFloat(tombstoneMapping(tombstone.Item, tombstoneUIElement))) || "N/A"
                                    }),
                                    tombstoneUIElement !== "marketCap" && /*#__PURE__*/ _jsx("span", {
                                        children: tombstoneMapping(tombstone.Item, tombstoneUIElement) || "N/A"
                                    })
                                ]
                            }, index);
                        })
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "p-2 col-span-1 grid grid-cols-1 gap-1",
                        children: [
                            ecpComponent && new Date().getFullYear() - parseInt((_a = tombstone === null || tombstone === void 0 ? void 0 : tombstone.Item) === null || _a === void 0 ? void 0 : _a.YearFounded, 10) <= 5 && /*#__PURE__*/ _jsx("div", {
                                className: "mb-2",
                                dangerouslySetInnerHTML: {
                                    __html: ecpHtml
                                }
                            }),
                            (tombstoneMessage === null || tombstoneMessage === void 0 ? void 0 : tombstoneMessage.html) && /*#__PURE__*/ _jsx("div", {
                                dangerouslySetInnerHTML: {
                                    __html: updatedHtml
                                }
                            })
                        ]
                    })
                ]
            })
        ]
    });
};
export default Tombstone;
